import * as turf from "@turf/turf";
import type { GameClient } from "../net";
import type { GameMap } from "../map";
import { STATIONS } from "../stations";
import { radarEliminate, thermometerEliminate, polygonEliminate, halfPlaneEliminate, cutOptions, cutLine, freehandSlicePolys } from "../elimination";
import type { LngLat } from "../elimination";

// Question toolbar + flows. Seeker pins are now draggable: a tool marker drops
// on the map and the preview (ring / bisector / which stations are eliminated)
// updates live as you drag. Commit sends an op to the server; undo is global.

const DISTANCES = [
  { label: "¼", miles: 0.25 }, { label: "½", miles: 0.5 }, { label: "1", miles: 1 },
  { label: "1.5", miles: 1.5 }, { label: "2", miles: 2 }, { label: "3", miles: 3 },
  { label: "5", miles: 5 }, { label: "10", miles: 10 },
];
type Tool = "radar" | "thermometer" | "measure" | "paint" | "cut" | "draw" | null;

const TOOL_DEFS: { id: Exclude<Tool, null>; label: string; desc: string }[] = [
  { id: "radar", label: "Radar", desc: "“Are you within X miles of here?” — grey out everything outside (or inside)." },
  { id: "cut", label: "Cut", desc: "Split the map along a street/line and eliminate one side." },
  { id: "thermometer", label: "Thermo", desc: "“Am I getting hotter or colder?” between two points." },
  { id: "draw", label: "Draw", desc: "Lasso an area freehand, then keep or eliminate inside it." },
  { id: "measure", label: "Measure", desc: "Measure a distance — nothing is eliminated." },
];

export function mountQuestions(mount: HTMLElement, client: GameClient, gameMap: GameMap, opts?: { exclude?: Tool[]; onActive?: (label: string | null) => void; setFullscreen?: (on: boolean) => void; onPreview?: (count: number, commit: (() => void) | null) => void }) {
  let active: Tool = null;
  let cleanup: (() => void) | null = null;
  const toolDefs = TOOL_DEFS.filter((t) => !opts?.exclude?.includes(t.id));
  // Report the live preview count + a commit fn so the shell's on-map tool badge
  // can show "Eliminate N" without re-opening the card.
  const preview = (n: number, commit: (() => void) | null) => opts?.onPreview?.(n, commit);
  // "Draw on full map" affordance — collapses the sheet so a line/area tool gets
  // the whole map; the on-map tool badge keeps Eliminate/Undo reachable.
  const fullMapBtn = () => (opts?.setFullscreen ? `<button class="ghost q-fullmap" data-fullmap>⤢ Draw on full map</button>` : "");
  const wireFullMap = (body: HTMLElement) => body.querySelector("[data-fullmap]")?.addEventListener("click", () => opts?.setFullscreen?.(true));
  // Freehand "Draw a shape" wants the whole map, so selecting it collapses the
  // bottom sheet; finishing the stroke re-opens the card to keep/eliminate.
  const FULLSCREEN_TOOLS: Tool[] = ["draw"];
  const toolLabel = (t: Tool) => TOOL_DEFS.find((d) => d.id === t)?.label ?? null;

  function reset() {
    cleanup?.(); cleanup = null;
    gameMap.clearToolMarkers();
    gameMap.clearPreviewGeometry();
    gameMap.clearPreviewEliminations();
    gameMap.captureClicks = false;
    gameMap.onMapClick = null;
    gameMap.onToolExit = null;
    preview(0, null);
  }
  function setTool(t: Tool) {
    reset(); active = active === t ? null : t;
    gameMap.onToolExit = active ? () => setTool(null) : null; // map's Exit-tool button drops the tool

    opts?.onActive?.(active ? toolLabel(active) : null);
    opts?.setFullscreen?.(!!active && FULLSCREEN_TOOLS.includes(active));
    render();
  }
  function commit(ids: string[], text: string) { client.eliminate(ids, text); setTool(null); }
  function commitCut(ids: string[], text: string) { client.cut(ids, text); setTool(null); }
  function newOnly(ids: string[]): string[] { return ids.filter((id) => client.statusOf(id) === "active"); }
  function center(): [number, number] { const c = gameMap.map.getCenter(); return [c.lng, c.lat]; }

  // Shared card pieces so every Ask tool matches the Radar prototype card.
  function qPrev(n: number): string {
    return `<div class="q-prev"><span>PREVIEW · GREYING OUT</span><b>${n}</b></div>`;
  }
  function qRow(): string {
    return `<div class="q-row"><button class="ghost" data-undo2 ${client.board.canUndo ? "" : "disabled"}>Undo last</button><button class="ghost" data-reset>Reset board</button></div>`;
  }
  function wireQRow(body: HTMLElement) {
    body.querySelector("[data-undo2]")?.addEventListener("click", () => client.undoLast());
    body.querySelector("[data-reset]")?.addEventListener("click", () => { if (confirm("Reset the whole board?")) client.reset(); });
  }
  // Guard against committing with the tool's pin(s) still at the default map
  // centre — the #1 source of accidental wrong eliminations (you ask about a
  // place, then forget to drag the pin there). Returns false to abort.
  function placed(moved: boolean): boolean {
    return moved || confirm("The pin hasn't been moved from the map's centre yet — drag it onto your reference point first.\n\nEliminate anyway?");
  }
  // Couple the tool to the hider Q&A: send the exact question (categorised so
  // the hider gets matching quick-answer chips), then the seeker applies the
  // answer with the tool. Only shown when this player has a hider thread.
  function askBtn(): string {
    return client.seekerKey ? `<button class="ghost" data-ask>📩 Ask the hider this</button>` : "";
  }
  function wireAsk(body: HTMLElement, text: string, category: string) {
    const b = body.querySelector<HTMLButtonElement>("[data-ask]");
    b?.addEventListener("click", () => { client.postMessage(client.seekerKey, text, category); b.textContent = "✓ Asked — see Plan tab"; b.disabled = true; });
  }

  function render() {
    mount.innerHTML = `
      <div class="tools">
        ${toolDefs.map((t) => btn(t.id, t.label)).join(" ")}
        <button class="tool undo" data-undo ${client.board.canUndo ? "" : "disabled"}>Undo</button>
      </div>
      <div id="tool-body" class="tool-body"></div>`;
    mount.querySelectorAll<HTMLButtonElement>("button.tool[data-tool]").forEach((b) => b.addEventListener("click", () => setTool(b.dataset.tool as Tool)));
    mount.querySelector<HTMLButtonElement>("[data-undo]")!.addEventListener("click", () => client.undoLast());
    const body = mount.querySelector<HTMLElement>("#tool-body")!;
    if (active === null) {
      body.innerHTML = `<div class="tool-legend"><div class="tool-legend-h">Pick a tool to narrow down where the hider is:</div>${toolDefs.map((t) => `<div class="tool-legend-row"><b>${t.label}</b><span>${t.desc}</span></div>`).join("")}</div>`;
    }
    if (active === "radar") cleanup = radar(body);
    else if (active === "thermometer") cleanup = thermometer(body);
    else if (active === "measure") cleanup = measure(body);
    else if (active === "cut") cleanup = cut(body);
    else if (active === "draw") cleanup = draw(body);
  }
  function btn(t: Exclude<Tool, null>, label: string) {
    return `<button class="tool ${active === t ? "on" : ""}" data-tool="${t}">${label}</button>`;
  }

  // ---- Radar -------------------------------------------------------------
  function radar(body: HTMLElement): () => void {
    let dist = 5, answer: "yes" | "no" = "no", moved = false;
    const seeker: LngLat = center();
    gameMap.addToolMarker(seeker, "#c0392b", true, (ll) => { seeker[0] = ll[0]; seeker[1] = ll[1]; moved = true; recompute(); });
    const ids = () => newOnly(radarEliminate(seeker, dist, answer));
    function recompute() {
      gameMap.setPreviewGeometry([turf.circle(seeker, dist, { units: "miles", steps: 64 })]);
      gameMap.setPreviewEliminations(ids());
      paintUI();
    }
    function paintUI() {
      const n = ids().length;
      body.innerHTML = `
        <div class="q-q">“Are you within <span class="q-chip">${label(dist)} mi</span> of me?”<div class="q-sub">Drag the red pin on the map to your spot.</div></div>
        <div class="q-lbl">DISTANCE (MILES)</div>
        <div class="seg">${DISTANCES.map((d) => `<button data-d="${d.miles}" class="${d.miles === dist ? "on" : ""}">${d.label}</button>`).join("")}</div>
        <div class="q-lbl">THEIR ANSWER</div>
        <div class="seg"><button data-a="yes" class="${answer === "yes" ? "on" : ""}">Within</button><button data-a="no" class="${answer === "no" ? "on" : ""}">Outside</button></div>
        ${askBtn()}
        ${qPrev(n)}
        <button class="primary commit" data-commit>Eliminate ${n} stations</button>
        ${qRow()}`;
      const commitNow = () => { if (placed(moved)) commit(ids(), `Radar: ${answer === "yes" ? "within" : "not within"} ${label(dist)} mi`); };
      body.querySelectorAll<HTMLButtonElement>("[data-d]").forEach((b) => b.addEventListener("click", () => { dist = +b.dataset.d!; recompute(); }));
      body.querySelectorAll<HTMLButtonElement>("[data-a]").forEach((b) => b.addEventListener("click", () => { answer = b.dataset.a as "yes" | "no"; recompute(); }));
      wireAsk(body, `Are you within ${label(dist)} mi of me?`, "Radar");
      body.querySelector("[data-commit]")!.addEventListener("click", commitNow);
      wireQRow(body);
      preview(n, commitNow);
    }
    recompute();
    return () => {};
  }

  // ---- Thermometer -------------------------------------------------------
  function thermometer(body: HTMLElement): () => void {
    let answer: "hotter" | "colder" = "hotter", moved = false;
    const c = center();
    const a: LngLat = [c[0] - 0.01, c[1]]; const b: LngLat = [c[0] + 0.01, c[1]];
    // Labelled A/B handles centered on the points, connected by the preview line.
    const mkA = gameMap.addToolMarker(a, "#2980b9", true, (ll) => { a[0] = ll[0]; a[1] = ll[1]; moved = true; recompute(); }, true, "A");
    gameMap.addToolMarker(b, "#c0392b", true, (ll) => { b[0] = ll[0]; b[1] = ll[1]; moved = true; recompute(); }, true, "B");
    // "Start from" the A point: jump it to GPS, or drop it at the map centre and
    // go full-screen to place it.
    function startA(ll: LngLat) { a[0] = ll[0]; a[1] = ll[1]; moved = true; mkA.setLngLat(ll); recompute(); }
    function startFromGps() {
      navigator.geolocation?.getCurrentPosition(
        (p) => { startA([p.coords.longitude, p.coords.latitude]); gameMap.flyAboveSheet([p.coords.longitude, p.coords.latitude], 14); },
        () => alert("Couldn't get your location."));
    }
    function startDropPin() { const cc = gameMap.map.getCenter(); startA([cc.lng, cc.lat]); opts?.setFullscreen?.(true); }
    const ids = () => newOnly(thermometerEliminate(a, b, answer));
    function recompute() {
      gameMap.setPreviewGeometry([turf.lineString([a, b])]);
      gameMap.setPreviewEliminations(ids());
      paintUI();
    }
    function paintUI() {
      const n = ids().length;
      const span = fmt(turf.distance(a, b, { units: "miles" }));
      body.innerHTML = `
        <div class="q-q"><span style="color:var(--blue)">A</span> = start, <span style="color:var(--red)">B</span> = end · <b>${span}</b> apart<div class="q-sub">Drag the A &amp; B handles on the map, then say hotter or colder.</div></div>
        <div class="q-lbl">START (A) FROM</div>
        <div class="seg"><button data-starta="gps">📍 My location</button><button data-starta="pin">📌 Drop a pin</button></div>
        ${fullMapBtn()}
        <div class="q-lbl">THEIR ANSWER</div>
        <div class="seg"><button data-a="hotter" class="${answer === "hotter" ? "on" : ""}">Hotter</button><button data-a="colder" class="${answer === "colder" ? "on" : ""}">Colder</button></div>
        ${askBtn()}
        ${qPrev(n)}
        <button class="primary commit" data-commit>Eliminate ${n} stations</button>
        ${qRow()}`;
      const commitNow = () => { if (placed(moved)) commit(ids(), `Thermometer: ${answer}`); };
      body.querySelectorAll<HTMLButtonElement>("[data-a]").forEach((btn) => btn.addEventListener("click", () => { answer = btn.dataset.a as "hotter" | "colder"; recompute(); }));
      wireAsk(body, "From blue A → red B, am I getting hotter or colder?", "Thermometer");
      body.querySelector("[data-starta='gps']")?.addEventListener("click", startFromGps);
      body.querySelector("[data-starta='pin']")?.addEventListener("click", startDropPin);
      body.querySelector("[data-commit]")!.addEventListener("click", commitNow);
      wireQRow(body); wireFullMap(body);
      preview(n, commitNow);
    }
    recompute();
    return () => {};
  }

  // ---- Measure (utility) -------------------------------------------------
  function measure(body: HTMLElement): () => void {
    let mode: "line" | "ring" = "line", radius = 5;
    const c = center();
    const a: LngLat = [c[0] - 0.01, c[1]]; const b: LngLat = [c[0] + 0.01, c[1]];
    function start() {
      gameMap.clearToolMarkers(); gameMap.clearPreviewGeometry();
      if (mode === "line") {
        gameMap.addToolMarker(a, "#2980b9", true, (ll) => { a[0] = ll[0]; a[1] = ll[1]; draw(); });
        gameMap.addToolMarker(b, "#c0392b", true, (ll) => { b[0] = ll[0]; b[1] = ll[1]; draw(); });
      } else {
        gameMap.addToolMarker(a, "#c0392b", true, (ll) => { a[0] = ll[0]; a[1] = ll[1]; draw(); });
      }
      draw();
    }
    function draw() {
      if (mode === "line") {
        gameMap.setPreviewGeometry([turf.lineString([a, b])]);
        result = fmt(turf.distance(a, b, { units: "miles" }));
      } else {
        gameMap.setPreviewGeometry([turf.circle(a, radius, { units: "miles", steps: 64 })]);
        result = `${label(radius)} mile ring`;
      }
      paintUI();
    }
    let result = "";
    function paintUI() {
      body.innerHTML = `
        <p class="hint">Quick distance gauge — nothing is eliminated.</p>
        <div class="seg"><button data-m="line" class="${mode === "line" ? "on" : ""}">Two-point</button><button data-m="ring" class="${mode === "ring" ? "on" : ""}">Radius ring</button></div>
        ${mode === "ring" ? `<div class="seg">${DISTANCES.map((d) => `<button data-r="${d.miles}" class="${d.miles === radius ? "on" : ""}">${d.label}</button>`).join("")}</div>` : ""}
        <div class="measure-result">${result}</div>`;
      body.querySelectorAll<HTMLButtonElement>("[data-m]").forEach((x) => x.addEventListener("click", () => { mode = x.dataset.m as "line" | "ring"; start(); }));
      body.querySelectorAll<HTMLButtonElement>("[data-r]").forEach((x) => x.addEventListener("click", () => { radius = +x.dataset.r!; draw(); }));
    }
    start();
    return () => {};
  }

  // ---- Manual cut: draw a line, eliminate one side -----------------------
  function cut(body: HTMLElement): () => void {
    const c = center();
    const a: LngLat = [c[0] - 0.012, c[1] + 0.006]; const b: LngLat = [c[0] + 0.012, c[1] - 0.006];
    let side: "above" | "below" | "left" | "right" | null = null, moved = false;
    // Centered handles so the two endpoints sit exactly ON the cut line.
    gameMap.addToolMarker(a, "#2980b9", true, (ll) => { a[0] = ll[0]; a[1] = ll[1]; moved = true; recompute(); }, true);
    gameMap.addToolMarker(b, "#2980b9", true, (ll) => { b[0] = ll[0]; b[1] = ll[1]; moved = true; recompute(); }, true);
    const ids = () => side ? newOnly(halfPlaneEliminate(a, b, side)) : [];
    function recompute() {
      const opts = cutOptions(a, b);
      if (side && !opts.includes(side)) side = null; // orientation flipped
      gameMap.setPreviewGeometry([cutLine(a, b)]);
      gameMap.setPreviewEliminations(ids());
      paintUI(opts);
    }
    function paintUI(opts: ("above" | "below" | "left" | "right")[]) {
      const lab = { above: "Above", below: "Below", left: "Left", right: "Right" };
      const n = ids().length;
      body.innerHTML = `
        <div class="q-q">Cut the board<div class="q-sub">Drag the line across the map, then pick which side to eliminate.</div></div>
        ${fullMapBtn()}
        <div class="q-lbl">ELIMINATE WHICH SIDE</div>
        <div class="seg">${opts.map((o) => `<button data-side="${o}" class="${side === o ? "on" : ""}">${lab[o]}</button>`).join("")}</div>
        ${qPrev(n)}
        <button class="primary commit" data-commit ${side ? "" : "disabled"}>Eliminate ${n} stations</button>
        ${qRow()}`;
      const commitNow = () => { if (side && placed(moved)) commitCut(ids(), `Manual cut: eliminated ${side}`); };
      body.querySelectorAll<HTMLButtonElement>("[data-side]").forEach((x) => x.addEventListener("click", () => { side = x.dataset.side as typeof side; recompute(); }));
      body.querySelector("[data-commit]")?.addEventListener("click", commitNow);
      wireQRow(body); wireFullMap(body);
      preview(side ? n : 0, side ? commitNow : null);
    }
    recompute();
    return () => {};
  }

  // ---- Freehand finger-draw: loop = lasso, line = slice a side ------------
  function draw(body: HTMLElement): () => void {
    let path: LngLat[] = [];
    let phase: "drawing" | "lasso" | "slice" = "drawing";
    let lassoMode: "keep-inside" | "eliminate-inside" = "keep-inside";
    let sides: [GeoJSON.Feature<GeoJSON.Polygon>, GeoJSON.Feature<GeoJSON.Polygon>] | null = null;
    let sideIdx = 0;
    let teardown: (() => void) | null = null;

    function poly() { return turf.polygon([[...path, path[0]].map((p) => [p[0], p[1]])]); }
    function ids(): string[] {
      if (phase === "lasso") return newOnly(polygonEliminate(poly(), lassoMode));
      if (phase === "slice" && sides) return newOnly(polygonEliminate(sides[sideIdx], "eliminate-inside"));
      return [];
    }
    function recompute() {
      if (phase === "lasso") gameMap.setPreviewGeometry([poly()]);
      else if (phase === "slice" && sides) gameMap.setPreviewGeometry([turf.lineString(path), sides[sideIdx]]);
      gameMap.setPreviewEliminations(ids());
      paintUI();
    }
    function finishDraw() {
      const closed = turf.distance(path[0], path[path.length - 1], { units: "miles" }) < 0.5 && path.length >= 8;
      if (closed) { phase = "lasso"; } else { phase = "slice"; sides = freehandSlicePolys(path); sideIdx = 0; }
      opts?.setFullscreen?.(false); // bring the card back to pick keep/eliminate
      recompute();
    }
    function start() {
      teardown?.();
      path = []; phase = "drawing"; sides = null;
      gameMap.clearPreviewGeometry(); gameMap.clearPreviewEliminations();
      teardown = gameMap.freehandDraw(
        (pts) => { path = pts; gameMap.setPreviewGeometry([turf.lineString(pts)]); },
        (pts) => { path = pts; finishDraw(); }
      );
      paintUI();
    }
    function paintUI() {
      if (phase === "drawing") { body.innerHTML = `<div class="q-q">✏️ Draw a shape on the map<div class="q-sub">The panel slid down for a full map. Drag across it: a closed loop selects inside; an open line slices off one side.</div></div>`; preview(0, null); return; }
      const n = ids().length;
      if (phase === "lasso") {
        body.innerHTML = `<div class="q-q">Loop drawn<div class="q-sub">Keep or eliminate inside it.</div></div>
          <div class="q-lbl">MODE</div>
          <div class="seg"><button data-m="keep-inside" class="${lassoMode === "keep-inside" ? "on" : ""}">Keep inside</button><button data-m="eliminate-inside" class="${lassoMode === "eliminate-inside" ? "on" : ""}">Eliminate inside</button></div>
          ${qPrev(n)}
          <button class="primary commit" data-commit>Eliminate ${n} stations</button>
          <div class="q-row"><button class="ghost" data-redraw>Redraw</button></div>`;
        body.querySelectorAll<HTMLButtonElement>("[data-m]").forEach((x) => x.addEventListener("click", () => { lassoMode = x.dataset.m as typeof lassoMode; recompute(); }));
      } else {
        body.innerHTML = `<div class="q-q">Line drawn<div class="q-sub">Eliminate the highlighted side, or switch.</div></div>
          ${qPrev(n)}
          <button class="primary commit" data-commit>Eliminate ${n} stations</button>
          <div class="q-row"><button class="ghost" data-switch>Switch side</button><button class="ghost" data-redraw>Redraw</button></div>`;
        body.querySelector("[data-switch]")!.addEventListener("click", () => { sideIdx = 1 - sideIdx; recompute(); });
      }
      const commitNow = () => commitCut(ids(), phase === "lasso" ? `Freehand: ${lassoMode === "keep-inside" ? "kept inside" : "eliminated inside"} a loop` : "Freehand: sliced off one side");
      body.querySelector("[data-commit]")?.addEventListener("click", commitNow);
      body.querySelector("[data-redraw]")?.addEventListener("click", start);
      preview(n, commitNow);
    }
    start();
    return () => teardown?.();
  }

  client.subscribe(() => { if (!active) render(); });
  render();

  // Tab isolation: the Ask tools share the map's tool-marker / preview layers
  // with the Measure tool, so when the Ask tab loses focus we tear the active
  // tool down (clears its pins + preview) instead of letting it linger or get
  // stomped by the other tab. setTool(null) resets to the bare tool palette.
  return { deactivate: () => setTool(null) };
}

function label(m: number): string { return DISTANCES.find((d) => d.miles === m)?.label ?? String(m); }
function fmt(mi: number): string { return mi < 0.2 ? `${Math.round(mi * 5280)} ft (${mi.toFixed(2)} mi)` : `${mi.toFixed(2)} mi`; }
export { STATIONS };
